import { BaseHttpService } from '@shared/abstractions/base-http.service';
import { Injectable } from '@angular/core';
import UserDto from '@shared/services/users/dto/user.dto';
import { UsersPagedQueryDto } from '@shared/services/users/dto/users-paged-query.dto';
import CreateUserDto from '@shared/services/users/dto/create-user.dto';
import UpdateUserDto from '@shared/services/users/dto/update-user.dto';
import { Observable } from 'rxjs';
import { HelpCrunchContact } from '@shared/widgets/help-crunch/help-crunch-widget.service';
import { UserAvatarsResponseDto } from '@shared/services/users/dto/user-avatars-response.dto';
import { BeautifiedAvatarResponseDto } from '@shared/services/users/dto/beautified-avatar-response.dto';
import { BeautifiedAvatarsPagedQueryDto } from '@shared/services/users/dto/beautified-avatars-paged-query.dto';
import BasePagedModel from '@shared/models/base-paged-model';
import UserPagedDto from './dto/user-paged.dto';

@Injectable({
  providedIn: 'root',
})
export default class UsersApiService extends BaseHttpService {
  constructor() {
    super('users');
  }

  public getAllPaged(query: UsersPagedQueryDto) {
    return this.getPaged<UserPagedDto, UsersPagedQueryDto>('', query);
  }

  public create(body: CreateUserDto) {
    return this.post<UserDto>('', body);
  }

  public updateById(id: string, body: UpdateUserDto) {
    return this.put<UserDto>(id, body);
  }

  public deleteById(id: string) {
    return this.delete(id);
  }

  public restoreById(id: string) {
    return this.post(`restore/${id}`);
  }

  public getContactInfoForHelpCrunchWidget(
    userId: string,
  ): Observable<HelpCrunchContact> {
    return this.get<HelpCrunchContact>(`${userId}/helpcrunch`);
  }

  public getAvatars(userId: string): Observable<UserAvatarsResponseDto> {
    return this.get<UserAvatarsResponseDto>(`${userId}/avatars`);
  }

  public uploadAvatar(userId: string, file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return this.post<string>(`${userId}/avatar`, formData, {
      responseType: 'text',
    });
  }

  public setPrimaryAvatar(userId: string, avatarId: string): Observable<void> {
    return this.put<void>(`${userId}/avatars/${avatarId}/primary`);
  }

  public getAvatarFile(userId: string, avatarId: string): Observable<Blob> {
    return this.get<Blob>(`${userId}/avatars/${avatarId}/file`, undefined, {
      responseType: 'blob',
    });
  }

  public deleteAvatar(userId: string, avatarId: string): Observable<void> {
    return this.delete<void>(`${userId}/avatars/${avatarId}`);
  }

  public getBeautifiedAvatars(
    dto: BeautifiedAvatarsPagedQueryDto,
  ): Observable<BasePagedModel<BeautifiedAvatarResponseDto>> {
    return this.getPaged<
      BeautifiedAvatarResponseDto,
      BeautifiedAvatarsPagedQueryDto
    >('beautified-avatars', dto);
  }

  public beautifyAvatar(
    userId: string,
    externalId?: string,
  ): Observable<{ taskId: string } | null> {
    return this.post<{ taskId: string } | null>(
      `${userId}/beautify-avatar/${externalId}`,
    );
  }
}
